import { DataPeriod } from '@prisma/client';
import { Card, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Skeleton } from '@zond/ui';
import { Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { stringToColor } from '../../reports/constants/chart-colors';
import { AppStat } from '../../reports/user-report.queries';

// Function to format URL by removing http://, https://, and www. prefixes
const formatUrl = (url: string): string => {
  return url
    .replace(/^https?:\/\//, '') // Remove http:// or https://
    .replace(/^www\./, ''); // Remove www.
};

interface WebsitesSectionProps {
  websites: AppStat[];
  range: DataPeriod;
  onRangeChange: (range: DataPeriod) => void;
  isLoading?: boolean;
  limit?: number;
}

function WebsitesSkeleton() {
  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <Skeleton className="h-5 w-40 mb-1" />
          <Skeleton className="h-4 w-24" />
        </div>
        <Skeleton className="h-9 w-[120px]" />
      </div>
      <div className="space-y-3 min-h-[200px]">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 p-2">
            <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
            <div className="flex-1 min-w-0 max-w-[calc(100%-120px)]">
              <div className="flex flex-col gap-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-1.5 w-full rounded-full" />
              </div>
            </div>
            <div className="text-right flex-shrink-0 w-[80px]">
              <Skeleton className="h-4 w-16 ml-auto" />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function WebsitesSection({
  websites,
  range,
  onRangeChange,
  isLoading,
  limit = 5,
}: WebsitesSectionProps) {
  const { t } = useTranslation('dashboard');

  if (isLoading) {
    return <WebsitesSkeleton />;
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="font-semibold">{t('websites.title')}</h3>
          <p className="text-sm text-muted-foreground">
            {range === DataPeriod.Daily && t('usage.today')}
            {range === DataPeriod.Weekly && t('usage.thisWeek')}
            {range === DataPeriod.Monthly && t('usage.thisMonth')}
          </p>
        </div>
        <Select value={range} onValueChange={(value) => onRangeChange(value as DataPeriod)}>
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder={t('period.placeholder')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={DataPeriod.Daily}>{t('period.today')}</SelectItem>
            <SelectItem value={DataPeriod.Weekly}>{t('period.thisWeek')}</SelectItem>
            <SelectItem value={DataPeriod.Monthly}>{t('period.thisMonth')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1 min-h-[200px]">
        {!isLoading && websites?.length === 0 ? (
          <div className="flex flex-col items-center justify-center w-full h-[200px] p-6 rounded-lg">
            <div className="h-12 flex items-center justify-center border rounded-lg p-2 mb-4">
              <Globe className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="flex flex-col items-center text-center">
              <h2 className="text-lg font-semibold mb-2">{t('websites.empty.title')}</h2>
              <p className="text-sm text-muted-foreground max-w-[300px]">{t('websites.empty.description')}</p>
            </div>
          </div>
        ) : (
          websites?.slice(0, limit).map((site) => {
            // Format the URL by removing prefixes
            const formattedUrl = formatUrl(site.name);

            return (
              <div key={site.id} className="flex items-center gap-3 p-2">
                <div className="h-8 w-8 flex-shrink-0 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {formattedUrl.slice(0, 2).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0 max-w-[calc(100%-120px)]">
                  <div className="flex flex-col gap-1.5">
                    <h4 className="text-sm truncate" title={site.name}>
                      {formattedUrl}
                    </h4>
                    <div className="w-full bg-muted rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full bg-primary"
                        style={{
                          width: `${site.usagePercentage}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 w-[80px]">
                  <p className="text-sm text-muted-foreground">{site.usageTime}</p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
}
